import * as asyncRoutes from './asyncRoutes';
import * as R from './routes';
import {
  AsyncAutoRepay,
  AsyncInsurancePolicy,
  AsyncNotFound,
  AsyncRejectedTimer,
  AsyncSaveCard,
} from './asyncRoutes';
import { INSURANCE_POLICY } from './routes';
import { isNotProduction } from 'utils/isNotProduction';
import { Loader } from 'components';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useClient } from 'hooks/webapi';
import { useEffect } from 'react';
import { useGTMModal } from 'hooks/utilities/useGTMModal';
import PrivateRoute from './PrivateRoute';
import PublicRoute from './PublicRoute';
import useSmartlook from 'hooks/useSmartlook';

export default function AppRouter() {
  const { client: clientData } = useClient();
  useGTMModal(); // GTM modal useEffects to show/hide GTM modal
  const { identifyClient } = useSmartlook();

  // Smartlook Identify Client
  useEffect(() => {
    if (!isNotProduction()) identifyClient();
  }, [clientData?.number]);

  return (
    <Routes>
      {/* PublicRoute should not be used as redirect specified on login itself based on user state */}
      <Route
        path={R.LOGIN}
        element={
          <PublicRoute
            fallback={<Loader />}
            component={asyncRoutes.AsyncLogin}
            path={R.LOGIN}
            key={R.LOGIN}
          />
        }
      />

      <Route
        path={R.TWO_FACTOR_AUTH}
        element={
          <PublicRoute
            path={R.TWO_FACTOR_AUTH}
            fallback={<Loader />}
            component={asyncRoutes.AsyncTwoFactorAuth}
            key={R.TWO_FACTOR_AUTH}
          />
        }
      />

      <Route
        path={R.CHANGE_PHONE_TWO_FACTOR_AUTH}
        element={
          <PublicRoute
            path={R.CHANGE_PHONE_TWO_FACTOR_AUTH}
            fallback={<Loader />}
            component={asyncRoutes.AsyncChangePhoneTwoFactorAuth}
            key={R.CHANGE_PHONE_TWO_FACTOR_AUTH}
          />
        }
      />

      <Route
        path={R.TWO_2FA_PHONE_ERROR}
        element={
          <PublicRoute
            path={R.TWO_2FA_PHONE_ERROR}
            fallback={<Loader />}
            component={asyncRoutes.AsyncTwoFAPhoneError}
            key={R.TWO_2FA_PHONE_ERROR}
          />
        }
      />

      <Route
        path={R.TWO_2FA_PHONE_MODIFIED}
        element={
          <PublicRoute
            path={R.TWO_2FA_PHONE_MODIFIED}
            fallback={<Loader />}
            component={asyncRoutes.AsyncTwo2FAPhoneModified}
            key={R.TWO_2FA_PHONE_MODIFIED}
          />
        }
      />

      <Route
        path={R.ONLINE_LOGIN}
        element={
          <PublicRoute
            path={R.ONLINE_LOGIN}
            fallback={<Loader />}
            component={asyncRoutes.AsyncLogin}
            key={R.ONLINE_LOGIN}
          />
        }
      />

      <Route
        path={R.ONE_TIME_TOKEN_LOGIN}
        element={
          <PublicRoute
            path={R.ONE_TIME_TOKEN_LOGIN}
            fallback={<Loader />}
            component={asyncRoutes.AsyncOneTimeTokenLogin}
            key={R.ONE_TIME_TOKEN_LOGIN}
          />
        }
      />

      <Route
        path={R.UNSUBSCRIPTION_PAGE}
        element={
          <PublicRoute
            path={R.UNSUBSCRIPTION_PAGE}
            fallback={<Loader />}
            component={asyncRoutes.AsyncUnsubscribePage}
            key={R.UNSUBSCRIPTION_PAGE}
          />
        }
      />

      <Route
        path={R.PASSWORD_CHANGE}
        element={
          <PrivateRoute
            path={R.PASSWORD_CHANGE}
            component={asyncRoutes.AsyncPasswordChange}
            fallback={<Loader />}
            key={R.PASSWORD_CHANGE}
          />
        }
      />

      <Route
        path={R.PASSWORD_RECOVERY}
        element={
          <PublicRoute
            path={R.PASSWORD_RECOVERY}
            fallback={<Loader />}
            component={asyncRoutes.AsyncPasswordRecovery}
            key={R.PASSWORD_RECOVERY}
          />
        }
      />

      <Route
        path={R.PASSWORD_RECOVERY_SUCCESS}
        element={
          <PublicRoute
            path={R.PASSWORD_RECOVERY_SUCCESS}
            fallback={<Loader />}
            component={asyncRoutes.AsyncPasswordRecoverySuccess}
            key={R.PASSWORD_RECOVERY_SUCCESS}
          />
        }
      />

      <Route
        path={R.LOAN_CONFIRM}
        element={
          <PrivateRoute
            path={R.LOAN_CONFIRM}
            fallback={<Loader />}
            component={asyncRoutes.AsyncLoanConfirm}
            key={R.LOAN_CONFIRM}
          />
        }
      />

      <Route
        path={R.ACTIVE_LOAN}
        element={
          <PrivateRoute
            path={R.ACTIVE_LOAN}
            fallback={<Loader />}
            component={asyncRoutes.AsyncActiveLoan}
            key={R.ACTIVE_LOAN}
          />
        }
      />

      <Route
        path={R.PROFILE}
        element={
          <PrivateRoute
            path={R.PROFILE}
            fallback={<Loader />}
            component={asyncRoutes.AsyncProfile}
            key={R.PROFILE}
          />
        }
      />

      <Route
        path={R.REPEATED_LOAN}
        element={
          <PrivateRoute
            path={R.REPEATED_LOAN}
            fallback={<Loader />}
            component={asyncRoutes.AsyncRepeatedLoan}
            key={R.REPEATED_LOAN}
          />
        }
      />

      <Route
        path={R.REPAYMENT}
        element={
          <PrivateRoute
            path={R.REPAYMENT}
            fallback={<Loader />}
            component={asyncRoutes.AsyncRepayment}
            key={R.REPAYMENT}
          />
        }
      />

      <Route
        path={R.FULL_REPAYMENT}
        element={
          <PrivateRoute
            path={R.FULL_REPAYMENT}
            fallback={<Loader />}
            component={asyncRoutes.AsyncFullRepayment}
            key={R.FULL_REPAYMENT}
          />
        }
      />

      <Route
        path={R.REPAYMENT_WIRE_TRANSFER}
        element={
          <PrivateRoute
            path={R.REPAYMENT_WIRE_TRANSFER}
            fallback={<Loader />}
            component={asyncRoutes.AsyncRepaymentWireTransfer}
            key={R.REPAYMENT_WIRE_TRANSFER}
          />
        }
      />

      <Route
        path={R.EXTENSION_WIRE_TRANSFER}
        element={
          <PrivateRoute
            path={R.EXTENSION_WIRE_TRANSFER}
            fallback={<Loader />}
            component={asyncRoutes.AsyncRepaymentWireTransfer}
            typeOfPayment="EXTENSION"
            backUrl={R.EXTENSION_PAYMENT}
            key={R.EXTENSION_WIRE_TRANSFER}
          />
        }
      />

      <Route
        path={R.EXTENSIONS}
        element={
          <PrivateRoute
            path={R.EXTENSIONS}
            fallback={<Loader />}
            component={asyncRoutes.AsyncExtensions}
            key={R.EXTENSIONS}
          />
        }
      />

      <Route
        path={R.EXTENSION_PAYMENT}
        element={
          <PrivateRoute
            path={R.EXTENSION_PAYMENT}
            fallback={<Loader />}
            component={asyncRoutes.AsyncExtensionPayment}
            key={R.EXTENSION_PAYMENT}
          />
        }
      />

      <Route
        path={R.CHECK_DOCUMENTS}
        element={
          <PrivateRoute
            path={R.CHECK_DOCUMENTS}
            fallback={<Loader />}
            component={asyncRoutes.AsyncCheckDocuments}
            key={R.CHECK_DOCUMENTS}
          />
        }
      />

      <Route
        path={R.EXTENSION_SUCCESS}
        element={
          <PrivateRoute
            path={R.EXTENSION_SUCCESS}
            fallback={<Loader />}
            component={asyncRoutes.AsyncExtensionSuccess}
            key={R.EXTENSION_SUCCESS}
          />
        }
      />

      <Route
        path={R.ADDITIONAL_AMOUNT}
        element={
          <PrivateRoute
            path={R.ADDITIONAL_AMOUNT}
            fallback={<Loader />}
            component={asyncRoutes.AsyncAdditionalAmount}
            key={R.ADDITIONAL_AMOUNT}
          />
        }
      />

      <Route
        path={R.LOAN_CHECK_DETAILS}
        element={
          <PrivateRoute
            path={R.LOAN_CHECK_DETAILS}
            fallback={<Loader />}
            component={asyncRoutes.AsyncLoanCheckDetails}
            typeOfLoan="MAIN"
            key={R.LOAN_CHECK_DETAILS}
          />
        }
      />

      <Route
        path={R.ADDITIONAL_AMOUNT_CHECK_DETAILS}
        element={
          <PrivateRoute
            path={R.ADDITIONAL_AMOUNT_CHECK_DETAILS}
            fallback={<Loader />}
            component={asyncRoutes.AsyncLoanCheckDetails}
            typeOfLoan="ADDITIONAL"
            key={R.ADDITIONAL_AMOUNT_CHECK_DETAILS}
          />
        }
      />

      <Route
        path={R.LOAN_CHECK_DETAILS_INSURANCE}
        element={
          <PrivateRoute
            path={R.LOAN_CHECK_DETAILS_INSURANCE}
            fallback={<Loader />}
            component={asyncRoutes.AsyncLoanCheckDetailsInsurance}
            typeOfLoan="MAIN"
            key={R.LOAN_CHECK_DETAILS_INSURANCE}
          />
        }
      />

      <Route
        path={R.ADDITIONAL_LOAN_CHECK_DETAILS_INSURANCE}
        element={
          <PrivateRoute
            path={R.ADDITIONAL_LOAN_CHECK_DETAILS_INSURANCE}
            fallback={<Loader />}
            component={asyncRoutes.AsyncLoanCheckDetailsInsurance}
            typeOfLoan="ADDITIONAL"
            key={R.ADDITIONAL_LOAN_CHECK_DETAILS_INSURANCE}
          />
        }
      />

      <Route
        path={R.ADDITIONAL_AMOUNT_CONFIRM}
        element={
          <PrivateRoute
            path={R.ADDITIONAL_AMOUNT_CONFIRM}
            fallback={<Loader />}
            component={asyncRoutes.AsyncLoanConfirm}
            typeOfLoan="ADDITIONAL"
            backUrl={R.ADDITIONAL_AMOUNT}
            key={R.ADDITIONAL_AMOUNT}
          />
        }
      />

      <Route
        path={R.CARD_PAYMENT}
        element={
          <PrivateRoute
            path={R.CARD_PAYMENT}
            fallback={<Loader />}
            component={asyncRoutes.AsyncCardPayment}
            key={R.CARD_PAYMENT}
          />
        }
      />

      <Route
        path={R.CARD_PAYMENT_CONS_SUCCESS}
        element={
          <PrivateRoute
            path={R.CARD_PAYMENT_CONS_SUCCESS}
            fallback={<Loader />}
            component={asyncRoutes.AsyncCardPaymentConsSuccess}
            key={R.CARD_PAYMENT_CONS_SUCCESS}
          />
        }
      />

      <Route
        path={R.CARD_PAYMENT_MINI_SUCCESS}
        element={
          <PrivateRoute
            path={R.CARD_PAYMENT_MINI_SUCCESS}
            fallback={<Loader />}
            component={asyncRoutes.AsyncCardPaymentMiniSuccess}
            key={R.CARD_PAYMENT_MINI_SUCCESS}
          />
        }
      />

      <Route
        path={R.DOCUMENTS_APPROVED}
        element={
          <PrivateRoute
            path={R.DOCUMENTS_APPROVED}
            fallback={<Loader />}
            component={asyncRoutes.AsyncDocumentsApproved}
            key={R.DOCUMENTS_APPROVED}
          />
        }
      />

      <Route
        path={R.DOCUMENTS_PENDING}
        element={
          <PrivateRoute
            path={R.DOCUMENTS_PENDING}
            fallback={<Loader />}
            component={asyncRoutes.AsyncDocumentsPending}
            key={R.DOCUMENTS_PENDING}
          />
        }
      />

      <Route
        path={R.EXTENSION_WITH_CARD}
        element={
          <PrivateRoute
            path={R.EXTENSION_WITH_CARD}
            fallback={<Loader />}
            component={asyncRoutes.AsyncExtensionCardPayment}
            key={R.EXTENSION_WITH_CARD}
          />
        }
      />

      <Route
        path={R.LOAN_APPROVED}
        element={
          <PrivateRoute
            path={R.LOAN_APPROVED}
            fallback={<Loader />}
            component={asyncRoutes.AsyncLoanApproved_v2}
            typeOfLoan="MAIN"
            key={R.LOAN_APPROVED}
          />
        }
      />

      <Route
        path={R.ADDITIONAL_AMOUNT_APPROVED}
        element={
          <PrivateRoute
            path={R.ADDITIONAL_AMOUNT_APPROVED}
            fallback={<Loader />}
            component={asyncRoutes.AsyncLoanApproved}
            typeOfLoan="ADDITIONAL"
            key={R.ADDITIONAL_AMOUNT_APPROVED}
          />
        }
      />

      <Route
        path={R.PASSWORD_CHANGE_SUCCESS}
        element={
          <PrivateRoute
            path={R.PASSWORD_CHANGE_SUCCESS}
            fallback={<Loader />}
            component={asyncRoutes.AsyncPasswordChangeSuccess}
            key={R.PASSWORD_CHANGE_SUCCESS}
          />
        }
      />

      <Route
        path={R.CHANGE_TEMPORARY_PASSWORD}
        element={
          <PrivateRoute
            path={R.CHANGE_TEMPORARY_PASSWORD}
            fallback={<Loader />}
            component={asyncRoutes.AsyncChangeTemporaryPassword}
            key={R.CHANGE_TEMPORARY_PASSWORD}
          />
        }
      />

      <Route
        path={R.SELEKTRA_ROUTE}
        element={
          <PrivateRoute
            path={R.SELEKTRA_ROUTE}
            fallback={<Loader />}
            component={asyncRoutes.AsyncSelektraPage}
            key={R.SELEKTRA_ROUTE}
          />
        }
      />

      <Route
        path={R.NEWS_CONSENT}
        element={
          <PrivateRoute
            path={R.NEWS_CONSENT}
            fallback={<Loader />}
            component={asyncRoutes.AsyncNewsConsent}
            key={R.NEWS_CONSENT}
          />
        }
      />

      <Route
        path={R.FAQ}
        element={
          <PrivateRoute
            path={R.FAQ}
            fallback={<Loader />}
            component={asyncRoutes.AsyncFAQ}
            key={R.FAQ}
          />
        }
      />

      {[R.ACCOUNT_BEST_OFFER, R.BEST_OFFER].map((route) => (
        <Route
          path={route}
          element={
            <PrivateRoute
              path={route}
              fallback={<Loader />}
              component={asyncRoutes.AsyncBestOffer}
              key={route}
            />
          }
        />
      ))}

      <Route
        path={R.SIGN_DR_AGREEMENT}
        element={
          <PrivateRoute
            path={R.SIGN_DR_AGREEMENT}
            fallback={<Loader />}
            component={asyncRoutes.AsyncSignDrAgreement}
            key={R.SIGN_DR_AGREEMENT}
          />
        }
      />

      <Route
        path={R.SIGN_DR_AGREEMENT_SUCCESS}
        element={
          <PrivateRoute
            path={R.SIGN_DR_AGREEMENT_SUCCESS}
            fallback={<Loader />}
            component={asyncRoutes.AsyncSignDrAgreementSuccess}
            key={R.SIGN_DR_AGREEMENT_SUCCESS}
          />
        }
      />

      <Route
        path={R.SAVE_CARD_MAIN}
        element={
          <PrivateRoute
            fallback={<Loader />}
            component={AsyncSaveCard}
            path={R.SAVE_CARD_MAIN}
            key={R.SAVE_CARD_MAIN}
            typeOfLoan="MAIN"
          />
        }
      />

      <Route
        path={R.SAVE_CARD_ADDITIONAL}
        element={
          <PrivateRoute
            fallback={<Loader />}
            component={AsyncSaveCard}
            path={R.SAVE_CARD_ADDITIONAL}
            key={R.SAVE_CARD_ADDITIONAL}
            typeOfLoan="ADDITIONAL"
          />
        }
      />

      <Route
        path={R.AUTOREPAY_ADDITIONAL}
        element={
          <PrivateRoute
            fallback={<Loader />}
            component={AsyncAutoRepay}
            path={R.AUTOREPAY_ADDITIONAL}
            key={R.AUTOREPAY_ADDITIONAL}
            typeOfLoan="ADDITIONAL"
          />
        }
      />

      <Route
        path={R.AUTOREPAY_MAIN}
        element={
          <PrivateRoute
            fallback={<Loader />}
            component={AsyncAutoRepay}
            path={R.AUTOREPAY_MAIN}
            key={R.AUTOREPAY_MAIN}
            typeOfLoan="MAIN"
          />
        }
      />

      <Route
        path={R.REPAYMENT_BROKEN_LOAN}
        element={
          <PrivateRoute
            path={R.REPAYMENT_BROKEN_LOAN}
            fallback={<Loader />}
            component={asyncRoutes.AsyncCardPaymentOverdueBrokenLoan}
            key={R.REPAYMENT_BROKEN_LOAN}
          />
        }
      />

      <Route
        path={R.BREAKDOWN_DEBT}
        element={
          <PrivateRoute
            path={R.BREAKDOWN_DEBT}
            fallback={<Loader />}
            component={asyncRoutes.AsyncBreakdownDebt}
            key={R.BREAKDOWN_DEBT}
          />
        }
      />

      <Route
        path={R.INSTALLMENT_PAYMENT}
        element={
          <PrivateRoute
            path={R.INSTALLMENT_PAYMENT}
            fallback={<Loader />}
            component={asyncRoutes.AsyncInstallmentPayment}
            key={R.INSTALLMENT_PAYMENT}
          />
        }
      />

      <Route
        path={R.DR_OFFER}
        element={
          <PrivateRoute
            path={R.DR_OFFER}
            fallback={<Loader />}
            component={asyncRoutes.AsyncDROffer}
            key={R.DR_OFFER}
          />
        }
      />

      <Route
        path={R.DR_OFFER_CONS}
        element={
          <PrivateRoute
            path={R.DR_OFFER_CONS}
            fallback={<Loader />}
            component={asyncRoutes.AsyncDROffer}
            key={R.DR_OFFER_CONS}
          />
        }
      />

      <Route
        path={R.DR_OVERDUE}
        element={
          <PrivateRoute
            path={R.DR_OVERDUE}
            fallback={<Loader />}
            component={asyncRoutes.AsyncDROverdue}
            key={R.DR_OVERDUE}
          />
        }
      />

      <Route
        path={R.DR_PAYMENT}
        element={
          <PrivateRoute
            path={R.DR_PAYMENT}
            fallback={<Loader />}
            component={asyncRoutes.AsyncDRPayment}
            key={R.DR_PAYMENT}
          />
        }
      />

      <Route
        path={R.DR_OFFER_ACCEPTED}
        element={
          <PrivateRoute
            path={R.DR_OFFER_ACCEPTED}
            fallback={<Loader />}
            component={asyncRoutes.AsyncDROfferAccepted}
            key={R.DR_OFFER_ACCEPTED}
          />
        }
      />

      <Route
        path={R.DR_PAYMENT_SCHEDULE}
        element={
          <PrivateRoute
            path={R.DR_PAYMENT_SCHEDULE}
            fallback={<Loader />}
            component={asyncRoutes.AsyncDRPaymentSchedule}
            key={R.DR_PAYMENT_SCHEDULE}
          />
        }
      />

      <Route
        path={R.BUNDLE_VIVUSPLUS_OFFER}
        element={
          <PrivateRoute
            path={R.BUNDLE_VIVUSPLUS_OFFER}
            fallback={<Loader />}
            component={asyncRoutes.AsyncVivusPlusOffer}
            key={R.BUNDLE_VIVUSPLUS_OFFER}
          />
        }
      />

      <Route
        path={R.BUNDLE_VIVUSPLUS_PENDING}
        element={
          <PrivateRoute
            path={R.BUNDLE_VIVUSPLUS_PENDING}
            fallback={<Loader />}
            component={asyncRoutes.AsyncVivusPlusOffer}
            key={R.BUNDLE_VIVUSPLUS_PENDING}
          />
        }
      />

      <Route
        path={R.BUNDLE_VIVUSPLUS_CONFIRMED}
        element={
          <PrivateRoute
            path={R.BUNDLE_VIVUSPLUS_CONFIRMED}
            fallback={<Loader />}
            component={asyncRoutes.AsyncBundleConfirmed}
            key={R.BUNDLE_VIVUSPLUS_CONFIRMED}
          />
        }
      />

      <Route
        path={R.BUNDLE_VIVUSPLUS_CONFIRMED_AA}
        element={
          <PrivateRoute
            path={R.BUNDLE_VIVUSPLUS_CONFIRMED_AA}
            fallback={<Loader />}
            component={asyncRoutes.AsyncBundleConfirmed}
            key={R.BUNDLE_VIVUSPLUS_CONFIRMED_AA}
          />
        }
      />

      <Route
        path={R.BUNDLE_VIVUSPLUS_NO_CONFIRMED_AA}
        element={
          <PrivateRoute
            path={R.BUNDLE_VIVUSPLUS_NO_CONFIRMED_AA}
            fallback={<Loader />}
            component={asyncRoutes.AsyncBundleNoConfirmed}
            key={R.BUNDLE_VIVUSPLUS_NO_CONFIRMED_AA}
          />
        }
      />

      <Route
        path={R.BUNDLE_VIVUSPLUS_NO_CONFIRMED}
        element={
          <PrivateRoute
            path={R.BUNDLE_VIVUSPLUS_NO_CONFIRMED}
            fallback={<Loader />}
            component={asyncRoutes.AsyncBundleNoConfirmed}
            key={R.BUNDLE_VIVUSPLUS_NO_CONFIRMED}
          />
        }
      />

      <Route
        path={R.BUNDLE_VIVUSPLUS_BENEFITS}
        element={
          <PrivateRoute
            path={R.BUNDLE_VIVUSPLUS_BENEFITS}
            fallback={<Loader />}
            component={asyncRoutes.AsyncBundleVivusPlusBenefits}
            key={R.BUNDLE_VIVUSPLUS_BENEFITS}
          />
        }
      />

      <Route
        path={R.BUNDLE_VIVUSPLUS_FAQ}
        element={
          <PrivateRoute
            path={R.BUNDLE_VIVUSPLUS_FAQ}
            fallback={<Loader />}
            component={asyncRoutes.AsyncBundleFAQ}
            key={R.BUNDLE_VIVUSPLUS_FAQ}
          />
        }
      />

      {[
        R.LOAN_REJECTED,
        R.ADDITIONAL_AMOUNT_REJECTED,
        R.LOAN_FAILED,
        R.ADDITIONAL_AMOUNT_FAILED,
        R.EXTENSION_REJECTED,
        R.CARD_PAYMENT_FAILURE,
        R.CARD_PAYMENT_CONS_FAILURE,
        R.CARD_PAYMENT_REJECTED_DUE_DUPLICATE,
        R.EXTENSION_CARD_PAYMENT_REJECTED_DUE_DUPLICATE,
        R.EXTENSION_LIMITS_REJECTED,
        R.CARD_PAYMENT_FAILURE_BY_CARD,
      ].map((route) => (
        <Route
          key={route}
          path={route}
          element={
            <PrivateRoute
              path={route}
              fallback={<Loader />}
              component={asyncRoutes.AsyncRejectedOrFailure}
            />
          }
        />
      ))}

      <Route
        path={R.DANA}
        element={
          <PrivateRoute
            path={R.DANA}
            fallback={<Loader />}
            component={asyncRoutes.AsyncDana}
          />
        }
      />

      {[R.REJECTED_TIMER, R.REJECTED_TIMER_FLOW].map((route) => (
        <Route path={route} element={<AsyncRejectedTimer />} />
      ))}

      <Route path={INSURANCE_POLICY} element={<AsyncInsurancePolicy />} />

      <Route path="*" element={<AsyncNotFound />} />

      <Route path="/" element={<Navigate to={R.LOGIN} replace />} />
    </Routes>
  );
}
